import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { ActivityApi } from 'src/app/shared/apis/activity.api';
import { BonusApi } from 'src/app/shared/apis/bonus.api';
import { SettingsApi } from 'src/app/shared/apis/settings.api';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { CSIconButtonComponent } from 'src/app/shared/components/cs-icon-button/cs-icon-button.component';
import { CSTabComponent } from 'src/app/shared/components/cs-tab/cs-tab.component';
import { EmptyModule } from 'src/app/shared/components/empty/empty.module';
import { LoadingModule } from 'src/app/shared/directive/loading/loading.module';
import { AccountInforData } from 'src/app/shared/interfaces/account.interface';
import { NewRankV2Record } from 'src/app/shared/interfaces/activity.interface';
import { UserNoticeItem } from 'src/app/shared/interfaces/notification-center.interface';
import { HandleInnerHtmlService } from 'src/app/shared/service/handle-inner-html.service';
import { NotificationService } from '../../notification.service';
import { DialogType, NotificationDialogService } from '../notification-dialog.service';

type NoticeType = 'Activity' | 'System' | 'Information' | 'Transaction';
@UntilDestroy()
@Component({
  selector: 'notification-overview-dialog',
  templateUrl: './overview.dialog.html',
  styleUrls: ['./overview.dialog.scss'],
  standalone: true,
  imports: [
    CSButtonComponent,
    CSIconButtonComponent,
    CSTabComponent,
    CommonModule,
    DatePipe,
    LoadingModule,
    EmptyModule,
  ],
})
export class NotificationOverviewDialog implements OnInit {
  private readonly customDocumentNoticeTypes = new Set([
    'CustomDocumentPendingVerification',
    'CustomDocumentApproved',
    'CustomDocumentRejected',
  ]);

  logined!: boolean; //是否登陆状态
  tabIndex$ = this.notificationDialogService.tabIndex$; //当前tab索引

  generalList: any[] = []; //常规通知列表

  promotionList: any[] = []; //推广列表

  tournamentList: (NewRankV2Record & { status: 'startDto' | 'endDto' | 'preDto' })[] = []; //赛事列表

  noticeListCount: number = 0; // 当前有多少信息未读

  loading = false; //加载状态

  filterTypes: NoticeType[] = [];
  private noticeConfigReady: Promise<void>;
  private readonly noticeTypeStorageKey = 'noticeTypeList';

  constructor(
    private notificationService: NotificationService,
    private activityApi: ActivityApi,
    private bounsApi: BonusApi,
    private appService: AppService,
    private router: Router,
    private dialogRef: MatDialogRef<NotificationOverviewDialog>,
    private notificationDialogService: NotificationDialogService,
    private settingsApi: SettingsApi,
    private handleInnerHtmlService: HandleInnerHtmlService,
  ) {
    this.noticeConfigReady = this.loadNoticeConfig();
  }

  ngOnInit(): void {
    this.appService.userInfo$.pipe(untilDestroyed(this)).subscribe((x: AccountInforData | null) => {
      this.logined = !!x;
    });
    this.appService.noticeCounts$.pipe(untilDestroyed(this)).subscribe(async e => {
      this.noticeListCount = e?.All || 0;

      if (this.logined) {
        await this.refreshOverviewData();
      }
      if (this.noticeListCount === 0) {
        this.notificationService.isReadedAll$.next(true);
      } else {
        this.notificationService.isReadedAll$.next(false);
      }
    });
    this.notificationService.reLoadData$.pipe(untilDestroyed(this)).subscribe(async () => {
      if (this.logined) {
        await this.refreshOverviewData();
      }
    });
  }

  private async refreshOverviewData() {
    this.loading = true;
    try {
      await this.updateGeneralList();
      await Promise.all([this.updatePromotionList(), this.updateTournament()]);
    } finally {
      this.loading = false;
    }
  }

  async updateGeneralList() {
    await this.noticeConfigReady;
    try {
      const list = await firstValueFrom(this.notificationService.getNoticeList());
      const filterTypes = this.filterTypes || [];
      this.generalList = filterTypes.length > 0 ? list.filter(item => filterTypes.includes(item.noticeType)) : list;
    } catch {
      this.generalList = [];
    }
  }

  async updatePromotionList() {
    try {
      const activityInfo = await firstValueFrom(this.bounsApi.getActivityInfo({ equipment: 'Web' }));
      this.promotionList = activityInfo.list[0]?.list || [];
    } catch {
      this.promotionList = [];
    }
  }

  async updateTournament() {
    try {
      const c = await firstValueFrom(
        this.activityApi.getNewRankV2List({
          startDto: { current: 1, size: 10, orderDirection: 'desc' },
          endDto: { current: 1, size: 10, orderDirection: 'desc' },
          preDto: { current: 1, size: 10, orderDirection: 'desc' },
        }),
      );
      const preItems = (c.perList?.records ?? []).map(r => ({ ...r, status: 'preDto' as const }));
      const startItems = (c.startList?.records ?? []).map(r => ({ ...r, status: 'startDto' as const }));
      const endItems = (c.endList?.records ?? []).map(r => ({ ...r, status: 'endDto' as const }));
      this.tournamentList = [...preItems, ...startItems, ...endItems];
    } catch {
      this.tournamentList = [];
    }
  }

  onTabChanged(event: number) {
    this.tabIndex$.next(event);
  }

  onGeneralClick(item: any) {
    this.notificationDialogService.detailTitle = item.title;
    this.notificationDialogService.detailContent = this.handleInnerHtmlService.handleSpecialChar(item.content);
    this.notificationDialogService.detailSendTime = item.sendTime;
    this.notificationDialogService.dialogType$.next(DialogType.DETAIL);

    // 打開的同時標示為已讀
    this.onReadClick(item);
  }

  onPromitionClick(item: any) {
    this.dialogRef.close();
    this.router.navigateByUrl(`/${this.appService.languageCode}/promotions/offer/${item.activitiesNo}`);
  }

  onTournamentClick(item: any) {
    if (!item?.tmpCode) {
      return;
    }
    this.dialogRef.close();
    this.router.navigateByUrl(`/${this.appService.languageCode}/promotions/tournament/${item.tmpCode}`);
  }

  onReadClick(item: any) {
    if (!item?.id || item.isReaded) {
      return;
    }
    this.notificationDialogService.onReadMsg(item.id);
  }

  onGeneralWalletClick() {
    this.dialogRef.close();
    void this.router.navigate([this.appService.languageCode, 'settings', 'funds']);
  }

  /** Backend KYC notices use their business type; text matching keeps older notices linkable. */
  isVerificationNotice(item: UserNoticeItem): boolean {
    if (this.customDocumentNoticeTypes.has(item.businessType)) {
      return false;
    }

    return /kyc|verification|document/i.test(`${item.businessType || ''} ${item.title || ''}`);
  }

  onGeneralVerificationClick(item: UserNoticeItem): void {
    this.onReadClick(item);
    this.dialogRef.close();
    void this.router.navigate([this.appService.languageCode, 'settings', 'verification']);
  }

  readAll() {
    const unreadIds = this.generalList.filter(item => item.id && !item.isReaded).map(item => item.id);
    if (unreadIds.length === 0) {
      return;
    }
    this.notificationDialogService.onReadAllMsg(unreadIds);
  }

  close() {
    this.dialogRef.close();
  }

  private async loadNoticeConfig() {
    const cachedTypes = this.getCachedNoticeTypes();
    if (cachedTypes.length > 0) {
      this.filterTypes = cachedTypes;
      return;
    }

    try {
      const noticeConfig = await firstValueFrom(this.settingsApi.getNoticeConfig());
      const remoteTypes = noticeConfig.data?.noticeTypeList || [];
      this.filterTypes = remoteTypes;
      this.cacheNoticeTypes(remoteTypes);
    } catch {
      // keep using cached filterTypes if remote load fails
    }
  }

  private getCachedNoticeTypes(): NoticeType[] {
    try {
      const raw = localStorage.getItem(this.noticeTypeStorageKey);
      return raw ? (JSON.parse(raw) as NoticeType[]) : [];
    } catch {
      return [];
    }
  }

  private cacheNoticeTypes(types: NoticeType[]) {
    try {
      localStorage.setItem(this.noticeTypeStorageKey, JSON.stringify(types));
    } catch {
      // ignore storage errors
    }
  }
}
